import type {
  CancelWishlistReservationResponse,
  CreateWishlistShareLinkResponse,
  DeleteWishlistItemResponse,
  DisableWishlistShareLinkResponse,
  GuestReservationConfirmedResponse,
  GuestReservationStartedResponse,
  ListWishlistInvitationsResponse,
  ListWishlistsOverviewResponse,
  PublicWishlist,
  PublicWishlistItem,
  RemoveWishlistGuestResponse,
  RotateWishlistShareLinkResponse,
  UpdateWishlistAccessPolicyResponse,
  WishlistAccessPolicyResponse,
  WishlistActivity as WishlistActivityDto,
  WishlistInvitedGuest,
  WishlistItem,
  WishlistManagerDetail,
  WishlistReservationManagerView,
  WishlistReservationSummary,
  WishlistShareLink,
  WishlistSummary,
} from '@nexus/api-types';
import type {
  WishlistAccessPolicyUpdateViewModel,
  WishlistAccessPolicyViewModel,
  WishlistActivityViewModel,
  WishlistDetailViewModel,
  WishlistGuestRemovalViewModel,
  WishlistInvitedGuestViewModel,
  WishlistItemDeletionViewModel,
  WishlistItemViewModel,
  WishlistOverviewSectionViewModel,
  WishlistOverviewViewModel,
  WishlistPublicItemViewModel,
  WishlistPublicViewModel,
  WishlistReservationCancellationViewModel,
  WishlistReservationConfirmationViewModel,
  WishlistReservationStartViewModel,
  WishlistReservationSummaryViewModel,
  WishlistReservationViewModel,
  WishlistShareLinkSecretViewModel,
  WishlistShareLinkViewModel,
  WishlistSummaryViewModel,
} from '@nexus/customer/wishlists/model';
import { resolveWishlistActionPermissions } from '@nexus/customer/wishlists/util';

type ShareLinkSecretResponse = CreateWishlistShareLinkResponse | RotateWishlistShareLinkResponse;

export function mapWishlistsOverviewResponseToViewModel(
  response: ListWishlistsOverviewResponse,
): WishlistOverviewViewModel {
  const managed: readonly WishlistSummaryViewModel[] = response.managed.map(
    (summary: WishlistSummary): WishlistSummaryViewModel => mapWishlistSummaryToViewModel(summary),
  );
  const beneficiary: readonly WishlistSummaryViewModel[] = response.beneficiary.map(
    (summary: WishlistSummary): WishlistSummaryViewModel => mapWishlistSummaryToViewModel(summary),
  );
  const household: readonly WishlistSummaryViewModel[] = response.household.map(
    (summary: WishlistSummary): WishlistSummaryViewModel => mapWishlistSummaryToViewModel(summary),
  );
  const sections: readonly WishlistOverviewSectionViewModel[] = [
    createOverviewSection('managed', managed),
    createOverviewSection('beneficiary', beneficiary),
    createOverviewSection('household', household),
  ];
  const allItems: readonly WishlistSummaryViewModel[] = [...managed, ...beneficiary, ...household];

  return {
    managed,
    beneficiary,
    household,
    sections,
    recentActivity: response.recentActivity.map(
      (activity: WishlistActivityDto): WishlistActivityViewModel => mapWishlistActivityToViewModel(activity),
    ),
    totalCount: allItems.length,
    activeCount: countSummariesByStatus(allItems, 'active'),
    archivedCount: countSummariesByStatus(allItems, 'archived'),
  };
}

export function mapWishlistSummaryToViewModel(summary: WishlistSummary): WishlistSummaryViewModel {
  return {
    id: summary.id,
    title: summary.title,
    description: summary.description ?? null,
    occasionLabel: summary.occasionLabel ?? null,
    occasionDate: summary.occasionDate ?? null,
    beneficiaryDisplayName: summary.beneficiary.displayName,
    visibility: summary.visibility,
    status: summary.status,
    surpriseMode: summary.surpriseMode,
    itemsCount: summary.itemsCount,
    reservedCount: summary.reservedCount,
    pendingCount: summary.pendingCount,
    availableCount: summary.reservedCount === null || summary.pendingCount === null
      ? null : Math.max(summary.itemsCount - summary.reservedCount - summary.pendingCount, 0),
    viewerRole: summary.viewerRole,
    permissions: resolveWishlistActionPermissions(summary.permissions),
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  };
}

export function mapWishlistManagerDetailToViewModel(detail: WishlistManagerDetail): WishlistDetailViewModel {
  return {
    id: detail.id,
    title: detail.title,
    description: detail.description ?? null,
    beneficiaryDisplayName: detail.beneficiaryDisplayName,
    beneficiaryKind: detail.beneficiaryKind,
    beneficiaryIsAccountBacked: detail.beneficiaryIsAccountBacked,
    occasionLabel: detail.occasionLabel ?? null,
    occasionDate: detail.occasionDate ?? null,
    visibility: detail.visibility,
    status: detail.status,
    surpriseMode: detail.surpriseMode,
    creatorUserId: detail.creatorUserId ?? null,
    creatorDisplayName: detail.creatorDisplayName ?? null,
    managerDisplayNames: detail.managerDisplayNames,
    viewerRole: detail.viewerRole,
    permissions: resolveWishlistActionPermissions(detail.permissions),
    items: detail.items.map((item: WishlistItem): WishlistItemViewModel => mapWishlistItemToViewModel(item)),
    shareLink: mapWishlistShareLinkToViewModel(detail.shareLink),
    reservationSummary: detail.reservationSummary ? mapReservationSummaryToViewModel(detail.reservationSummary) : null,
    reservations: detail.reservations.map(
      (reservation: WishlistReservationManagerView): WishlistReservationViewModel =>
        mapManagerReservationToViewModel(reservation),
    ),
    activity: detail.activity.map(
      (activity: WishlistActivityDto): WishlistActivityViewModel => mapWishlistActivityToViewModel(activity),
    ),
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
  };
}

export function mapWishlistActivityToViewModel(activity: WishlistActivityDto): WishlistActivityViewModel {
  return {
    id: activity.id,
    wishlistId: activity.wishlistId,
    wishlistTitle: activity.wishlistTitle,
    itemId: activity.itemId ?? null,
    itemTitle: activity.itemTitle ?? null,
    actorDisplayName: activity.actorDisplayName ?? null,
    type: activity.type,
    occurredAt: activity.occurredAt,
  };
}

export function mapWishlistAccessPolicyResponseToViewModel(
  response: WishlistAccessPolicyResponse,
): WishlistAccessPolicyViewModel {
  const shareLink: WishlistShareLinkViewModel = mapWishlistShareLinkToViewModel(response.shareLink);

  return {
    visibility: response.visibility,
    surpriseMode: response.surpriseMode,
    shareLink,
    canUsePublicLink: response.visibility === 'anyone_with_link' && shareLink.isActive,
  };
}

export function mapWishlistAccessPolicyUpdateResponseToViewModel(
  response: UpdateWishlistAccessPolicyResponse,
): WishlistAccessPolicyUpdateViewModel {
  return {
    accessPolicy: mapWishlistAccessPolicyResponseToViewModel(response.accessPolicy),
    wishlist: mapWishlistManagerDetailToViewModel(response.wishlist),
  };
}

export function mapWishlistShareLinkToViewModel(shareLink: WishlistShareLink): WishlistShareLinkViewModel {
  return {
    id: shareLink.id ?? null,
    status: shareLink.status,
    expiresAt: shareLink.expiresAt ?? null,
    disabledAt: shareLink.disabledAt ?? null,
    rotatedAt: shareLink.rotatedAt ?? null,
    createdAt: shareLink.createdAt ?? null,
    isActive: shareLink.status === 'active',
  };
}

export function mapWishlistShareLinkSecretResponseToViewModel(
  response: ShareLinkSecretResponse,
): WishlistShareLinkSecretViewModel {
  return {
    shareLink: mapWishlistShareLinkToViewModel(response.shareLink),
    shareToken: response.shareToken,
    shareUrl: response.shareUrl,
  };
}

export function mapDisabledWishlistShareLinkResponseToViewModel(
  response: DisableWishlistShareLinkResponse,
): WishlistShareLinkViewModel {
  return mapWishlistShareLinkToViewModel(response.shareLink);
}

export function mapWishlistInvitationsResponseToViewModels(
  response: ListWishlistInvitationsResponse,
): readonly WishlistInvitedGuestViewModel[] {
  return response.guests.map((guest: WishlistInvitedGuest): WishlistInvitedGuestViewModel =>
    mapWishlistInvitedGuestToViewModel(guest),
  );
}

export function mapWishlistInvitedGuestToViewModel(guest: WishlistInvitedGuest): WishlistInvitedGuestViewModel {
  return {
    id: guest.id,
    email: guest.email,
    invitedAt: guest.invitedAt,
    verifiedAt: guest.verifiedAt ?? null,
    isVerified: Boolean(guest.verifiedAt),
  };
}

export function mapWishlistPublicDtoToViewModel(wishlist: PublicWishlist): WishlistPublicViewModel {
  const items: readonly WishlistPublicItemViewModel[] = wishlist.items.map(
    (item: PublicWishlistItem): WishlistPublicItemViewModel => mapPublicWishlistItemToViewModel(item),
  );

  return {
    publicWishlistId: wishlist.publicWishlistId ?? null,
    title: wishlist.title ?? null,
    description: wishlist.description ?? null,
    beneficiaryDisplayName: wishlist.beneficiaryDisplayName ?? null,
    occasionLabel: wishlist.occasionLabel ?? null,
    occasionDate: wishlist.occasionDate ?? null,
    status: wishlist.status ?? null,
    accessStatus: wishlist.access.accessStatus,
    canRenderContents: wishlist.access.canRenderContents,
    items,
    hasReservableItems: items.some((item: WishlistPublicItemViewModel): boolean => item.canReserve),
  };
}

export function mapWishlistItemDeletionResponseToViewModel(
  response: DeleteWishlistItemResponse,
): WishlistItemDeletionViewModel {
  return {
    itemId: response.itemId,
    deleted: response.deleted,
  };
}

export function mapWishlistGuestRemovalResponseToViewModel(
  response: RemoveWishlistGuestResponse,
): WishlistGuestRemovalViewModel {
  return {
    guestId: response.guestId,
    removed: response.removed,
  };
}

export function mapWishlistReservationStartResponseToViewModel(
  response: GuestReservationStartedResponse,
): WishlistReservationStartViewModel {
  return {
    publicReservationId: response.publicReservationId,
    status: response.status,
    confirmationDeliveryHint: response.confirmationDeliveryHint,
    expiresAt: response.expiresAt,
    isPendingEmailConfirmation: response.status === 'pending_email_confirmation',
    ...(response.deliveryStatus ? { deliveryStatus: response.deliveryStatus } : {}),
  };
}

export function mapWishlistReservationConfirmationResponseToViewModel(
  response: GuestReservationConfirmedResponse,
): WishlistReservationConfirmationViewModel {
  return {
    status: response.status,
    publicWishlistId: response.publicWishlistId,
    publicItemId: response.publicItemId,
    isConfirmed: true,
    isAlreadyConfirmed: response.alreadyConfirmed === true,
  };
}

export function mapWishlistReservationCancellationResponseToViewModel(
  response: CancelWishlistReservationResponse,
): WishlistReservationCancellationViewModel {
  return {
    reservationId: response.reservationId,
    status: response.status,
    cancelledAt: response.cancelledAt,
    isCancelled: true,
  };
}

function mapWishlistItemToViewModel(item: WishlistItem): WishlistItemViewModel {
  return {
    id: item.id,
    wishlistId: item.wishlistId,
    title: item.title,
    description: item.description ?? null,
    url: item.url ?? null,
    imageUrl: item.imageUrl ?? null,
    priceHint: item.priceHint ?? null,
    privateNote: item.privateNote ?? null,
    priority: item.priority,
    status: item.status,
    position: item.position,
    reservationStatus: item.reservationStatus ?? null,
    availabilityStatus: item.availabilityStatus,
    canReserve: item.canReserve,
    isPendingReservation:
      item.reservationStatus === 'pending_email_confirmation' ||
      item.availabilityStatus === 'pending_email_confirmation',
    isConfirmedReservation: item.reservationStatus === 'confirmed' || item.availabilityStatus === 'reserved',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

function mapPublicWishlistItemToViewModel(item: PublicWishlistItem): WishlistPublicItemViewModel {
  return {
    publicItemId: item.publicItemId,
    title: item.title,
    description: item.description ?? null,
    url: item.url ?? null,
    imageUrl: item.imageUrl ?? null,
    priceHint: item.priceHint ?? null,
    priority: item.priority,
    availabilityStatus: item.availabilityStatus,
    canReserve: item.canReserve,
    isPendingReservation: item.availabilityStatus === 'pending_email_confirmation',
    isReserved: item.availabilityStatus === 'reserved',
  };
}

function mapReservationSummaryToViewModel(
  summary: WishlistReservationSummary,
): WishlistReservationSummaryViewModel {
  return {
    pendingCount: summary.pendingCount,
    confirmedCount: summary.confirmedCount,
    cancelledCount: summary.cancelledCount,
    expiredCount: summary.expiredCount,
    completedCount: summary.completedCount,
    activeCount: summary.pendingCount + summary.confirmedCount,
  };
}

function mapManagerReservationToViewModel(
  reservation: WishlistReservationManagerView,
): WishlistReservationViewModel {
  return {
    id: reservation.id,
    itemId: reservation.itemId,
    status: reservation.status,
    holderEmail: reservation.holderEmail ?? null,
    holderDisplayName: reservation.holderDisplayName ?? null,
    requestedAt: reservation.requestedAt,
    confirmedAt: reservation.confirmedAt ?? null,
    expiresAt: reservation.expiresAt,
    cancelledAt: reservation.cancelledAt ?? null,
    cancellationReason: reservation.cancellationReason ?? null,
    canCancel: reservation.canCancel,
    isPendingEmailConfirmation: reservation.status === 'pending_email_confirmation',
    isConfirmed: reservation.status === 'confirmed',
  };
}

function createOverviewSection(
  key: WishlistOverviewSectionViewModel['key'],
  items: readonly WishlistSummaryViewModel[],
): WishlistOverviewSectionViewModel {
  return {
    key,
    items,
    count: items.length,
  };
}

function countSummariesByStatus(
  summaries: readonly WishlistSummaryViewModel[],
  status: WishlistSummaryViewModel['status'],
): number {
  return summaries.filter((summary: WishlistSummaryViewModel): boolean => summary.status === status).length;
}
